import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentFixedIncomeData,
  type EnrollmentFixedIncomeSubmitInput,
  type EnrollmentFixedIncomeSubmitOutput,
} from '../../../models'
import { createMutationHook, createQueryHook } from '../../../hooks'

/**
 * Get Enrollment FixedIncome data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentFixedIncomeData>
 * @example const response = await getEnrollmentFixedIncomeData()
 */
export const getEnrollmentFixedIncomeData = async (context?: DefaultContext): Promise<EnrollmentFixedIncomeData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentFixedIncomeData',
      query: `mutation getEnrollmentFixedIncomeData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
            enrollmentData {
              experimentId
              userInfo {
                investment {
                  riskTolerance {
                    allocation {
                      equityPercent
                      fixedIncomePercent
                      cashEquivalentPercent
                    }
                  }
                }
              }
              recommendation {
                investment {
                  riskTolerance {
                    recommendedRiskTolerance
                    allowedRiskTolerances
                    riskTolerances {
                      category
                      index
                      allocation {
                        equityPercent
                        fixedIncomePercent
                        cashEquivalentPercent
                      }
                    }
                  }
                  fixedIncomeCategory {
                    type
                    maxAllocationPercent
                    recommendedRange {
                      recommendedPercent
                      allowedMinPercent
                      allowedMaxPercent
                    }
                  }
                }
              }
            }
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: {},
    },
    context,
  )) as { submitEnrollment: EnrollmentFixedIncomeData }
  return response.submitEnrollment
}

export const useGetEnrollmentFixedIncomeData = createQueryHook<undefined, EnrollmentFixedIncomeData>(
  getEnrollmentFixedIncomeData,
)

/**
 * Submit Enrollment FixedIncome data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentFixedIncomeSubmitOutput>
 * @example const response = await submitEnrollmentFixedIncomeData();
 */
export const submitEnrollmentFixedIncomeData = async (
  enrollmentFixedIncomeSubmitInput: EnrollmentFixedIncomeSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentFixedIncomeSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentFixedIncomeData',
      query: `mutation submitEnrollmentFixedIncomeData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentFixedIncomeSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentFixedIncomeSubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentFixedIncomeData = createMutationHook<
  EnrollmentFixedIncomeSubmitInput,
  EnrollmentFixedIncomeSubmitOutput
>(submitEnrollmentFixedIncomeData)
